// Lets a user "mark resolved" an OBD health alert they've already handled
// (e.g. a check-engine code cleared at the shop) so it stops showing — even if
// the tracker's last snapshot still reports it. The acknowledgement is tied to
// the exact alert signature (title + verdict), so if the condition CHANGES
// (a new/different code, a higher temp), it counts as a new alert and shows
// again.
//
// Acks live on the DEVICE itself (attributes.healthAcks) so resolving on one tab
// or profile clears it for EVERY viewer of that car. We keep a localStorage
// mirror for instant, synchronous reads; mergeServerAcks() pulls the device's
// acks into that mirror on every fleet load so other profiles' resolves show up.
import { getAllDevices, updateDevice } from './traccar';

const KEY = 'dd-health-ack';
const get = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const save = (o) => { try { localStorage.setItem(KEY, JSON.stringify(o)); } catch { /* ignore */ } };

// Push the current local acks for one device up to the device's attributes so
// every profile/tab sees them. Fire-and-forget; the local mirror already applied.
async function persist(deviceId) {
  try {
    const local = get()[deviceId] || {};
    const devices = await getAllDevices();
    const d = devices.find((x) => String(x.id) === String(deviceId));
    if (!d) return;
    const attributes = { ...(d.attributes || {}), healthAcks: local };
    await updateDevice({ ...d, attributes });
  } catch { /* local mirror still applies until the next successful sync */ }
}

// How long "resolved" holds while the SAME condition keeps being reported by the
// device. After this window, if the fault is still active, the alarm returns so the
// owner is reminded it isn't actually fixed. Keys not listed here stay resolved
// permanently (until the signature changes). Resolving again starts a new window.
const SNOOZE_MS = {
  // A check-engine light is serious, so "resolved" only quiets it briefly. If the
  // code was genuinely cleared, the fault disappears and rearmCleared() drops the
  // ack entirely — so this short window only ever nags when the light is STILL on.
  'check-engine': 30 * 60 * 1000,     // 30 min
  'engine-temp': 30 * 60 * 1000,      // 30 min — overheating shouldn't stay hidden
  charging: 60 * 60 * 1000,           // 1h
  fuel: 2 * 60 * 60 * 1000,           // 2h
};

export function ackHealth(deviceId, title, signature) {
  const o = get();
  o[deviceId] = o[deviceId] || {};
  o[deviceId][title] = { sig: signature, at: Date.now() }; // remember WHEN it was resolved
  save(o);            // instant local apply (all tabs in this browser)
  persist(deviceId);  // sync to the car so every profile sees it
}
export function unackHealth(deviceId, title) {
  const o = get();
  if (o[deviceId]) { delete o[deviceId][title]; save(o); persist(deviceId); }
}
export function isAcked(deviceId, title, signature) {
  const o = get();
  const e = o[deviceId] && o[deviceId][title];
  if (!e) return false;
  if (typeof e === 'string') return e === signature; // legacy entries = permanent
  if (e.sig !== signature) return false;              // condition changed → not resolved
  const ttl = SNOOZE_MS[title];
  if (!ttl) return true;                              // no snooze window → stays resolved
  return (Date.now() - (e.at || 0)) < ttl;            // still within the snooze window
}

// RE-ARM: if a condition is no longer present (the fault codes went away, temp
// came back down, tank was filled), drop its "resolved" mark. That way, if the
// SAME problem ever returns, it alerts again everywhere instead of staying
// silently suppressed because the signature happens to match the old one.
// Call this whenever fresh readings come in for a device.
export function rearmCleared(deviceId, a = {}) {
  const o = get();
  const acks = o[deviceId];
  if (!acks) return;
  let changed = false;
  const clear = (key) => { if (acks[key]) { delete acks[key]; changed = true; } };

  const dtcCount = Number(a.io30 || 0);
  if (!dtcCount) clear('check-engine');            // no active codes → re-arm
  const temp = Number(a.io32);
  if (!isNaN(temp) && temp > 0 && temp < 105) clear('engine-temp');
  const fuel = Number(a.io48);
  if (!isNaN(fuel) && fuel >= 15) clear('fuel');
  const power = Number(a.power);
  if (!isNaN(power) && power >= 12.2) clear('charging');

  if (changed) { save(o); persist(deviceId); }
}

// Pull each device's server-side acks into the local mirror. Call this right
// after loading the fleet/devices on any page, so resolves made on other
// profiles/browsers take effect here too. Server state wins on conflicts.
export function mergeServerAcks(devices) {
  const o = get();
  let changed = false;
  (devices || []).forEach((d) => {
    // raw devices carry it under attributes.healthAcks; getFleet objects expose it directly
    const server = (d && d.attributes && d.attributes.healthAcks) || (d && d.healthAcks);
    if (server && typeof server === 'object') {
      o[d.id] = { ...(o[d.id] || {}), ...server };
      changed = true;
    }
  });
  if (changed) save(o);
}

// When an alert is marked resolved, the card should read like a normal healthy
// one ("everything OK") rather than a struck-through alert. This returns the
// clean good-state text for a given alert key, or null for keys that simply
// shouldn't show a card in the healthy state (e.g. warning-light history).
export function resolvedDisplay(ackKey) {
  switch (ackKey) {
    case 'check-engine': return { verdict: 'No active fault codes', detail: 'The engine computer reports no active problems.' };
    case 'engine-temp': return { verdict: 'Temperature OK', detail: 'Marked resolved — reading no longer flagged.' };
    case 'charging': return { verdict: 'Charging OK', detail: 'Charging system looks fine.' };
    case 'fuel': return { verdict: 'Fuel OK', detail: 'Fuel level is fine.' };
    case 'mil-history': return null; // no card in the healthy state
    default: return { verdict: 'Resolved', detail: 'Marked resolved.' };
  }
}

export function attachAckSig(items, a = {}) {
  const map = {
    'Check Engine': ['check-engine', a.io30],
    'Engine Temperature': ['engine-temp', a.io32],
    'Battery & Charging': ['charging', a.power != null ? a.power.toFixed(1) : ''],
    Fuel: ['fuel', a.io48],
    'Warning-Light History': ['mil-history', a.io43],
  };
  items.forEach((c) => { const m = map[c.title]; if (m) { c.ackKey = m[0]; c.ackSig = String(m[1]); } });
  return items;
}

// Is the check-engine alert resolved for this device right now? Small helper so
// the RAW readings (fault-code count, dtcs) can show cleared in harmony with the
// health cards. Uses the same ackKey + signature (io30 count) as everything else.
export function checkEngineResolved(deviceId, a = {}) {
  return isAcked(deviceId, 'check-engine', String(a.io30));
}
